import { computeJustifiedLayout } from './justifiedLayout'
import { computeMasonryLayout } from './masonryLayout'
import { groupByDay } from './groupByDay'

export interface GalleryItemLike {
  id: string
  width: number
  height: number
  date?: string | null
}

export type GalleryLayoutMode = 'justified' | 'masonry'

export interface GalleryTile<T> {
  item: T
  width: number
  height: number
  /** absolute x within the section content — set only in masonry mode */
  left?: number
}

export interface GalleryRow<T> {
  tiles: Array<GalleryTile<T>>
  height: number
  /** absolute y within the gallery container */
  top: number
  /** index of the first tile within the flat photos array */
  startIndex: number
}

export interface GallerySection<T> {
  key: string | null
  rows: Array<GalleryRow<T>>
  top: number
  height: number
  startIndex: number
  /** height of the tile area (section height minus header and trailing gap) */
  contentHeight: number
}

export interface GalleryLayout<T> {
  sections: Array<GallerySection<T>>
  totalHeight: number
  gap: number
  targetRowHeight: number
  layoutMode: GalleryLayoutMode
}

// All heights are fixed numbers (headers included) so that y-offsets are computable
// without DOM measurement — visible-range mapping and scroll anchoring rely on this.
export const galleryMetrics = {
  headerHeight: 56,
  headerlessGap: 8,
  targetRowHeight: (containerWidth: number): number =>
    containerWidth < 640 ? 160 : containerWidth < 1024 ? 200 : 240,
  gap: (containerWidth: number): number => (containerWidth < 768 ? 4 : 8),
  /** the legacy masonry used wider gutters */
  masonryGap: (containerWidth: number): number => (containerWidth < 768 ? 4 : 20),
}

export interface BuildSectionsOptions {
  /** false → one continuous headerless stream (e.g. search results) */
  groupByDate?: boolean
  layoutMode?: GalleryLayoutMode
}

export const buildSections = <T extends GalleryItemLike>(
  items: Array<T>,
  containerWidth: number,
  { groupByDate = true, layoutMode = 'justified' }: BuildSectionsOptions = {}
): GalleryLayout<T> => {
  const gap =
    layoutMode === 'masonry'
      ? galleryMetrics.masonryGap(containerWidth)
      : galleryMetrics.gap(containerWidth)
  const targetRowHeight = galleryMetrics.targetRowHeight(containerWidth)

  if (containerWidth <= 0 || items.length === 0) {
    return { sections: [], totalHeight: 0, gap, targetRowHeight, layoutMode }
  }

  const sections: Array<GallerySection<T>> = []
  let cursor = 0
  let flatIndex = 0

  const dayGroups = groupByDate ? groupByDay(items) : [{ key: null, items }]
  for (const day of dayGroups) {
    const leading = day.key ? galleryMetrics.headerHeight : galleryMetrics.headerlessGap
    const sectionTop = cursor
    const contentTop = sectionTop + leading

    let rows: Array<GalleryRow<T>>
    let contentHeight: number

    if (layoutMode === 'masonry') {
      // one single-tile "row" per item, sorted by y so binary searches over row
      // tops keep working; startIndex still reflects the flat list order
      const layout = computeMasonryLayout(day.items, { containerWidth, gap })
      const sectionFlatStart = flatIndex
      rows = layout.tiles
        .map((tile, index) => ({
          tiles: [
            { item: day.items[index], width: tile.width, height: tile.height, left: tile.left },
          ],
          height: tile.height,
          top: contentTop + tile.top,
          startIndex: sectionFlatStart + index,
        }))
        .sort((a, b) => a.top - b.top)
      flatIndex += day.items.length
      contentHeight = layout.totalHeight
    } else {
      const layout = computeJustifiedLayout(day.items, { containerWidth, targetRowHeight, gap })
      // layout preserves item order, so tiles map back to day.items sequentially
      let localIndex = 0
      rows = layout.rows.map((row) => {
        const galleryRow: GalleryRow<T> = {
          tiles: row.tiles.map((tile) => ({
            item: day.items[localIndex++],
            width: tile.width,
            height: tile.height,
          })),
          height: row.height,
          top: contentTop + row.top,
          startIndex: flatIndex,
        }
        flatIndex += row.tiles.length
        return galleryRow
      })
      contentHeight = layout.totalHeight
    }

    const height = leading + contentHeight + gap
    sections.push({
      key: day.key,
      rows,
      top: sectionTop,
      height,
      contentHeight,
      startIndex: flatIndex - day.items.length,
    })
    cursor += height
  }

  return { sections, totalHeight: cursor, gap, targetRowHeight, layoutMode }
}
